import { EditorStarterItem } from '../item-type';
import type {GroupItem} from './group-item-type';

export interface GroupGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
  from: number;
  durationInFrames: number;
}

/**
 * 그룹(GroupItem)의 위치·크기·시간 범위를 children union 으로 계산한다.
 * GroupItem 은 bbox/from/duration 을 SoT 로 저장하지 않으므로, 렌더·선택 시 이 함수로
 * children 의 현재 값으로부터 재계산한다(children 변경이 자동 반영됨).
 * 멤버가 없거나 모두 누락이면 0 geometry 를 반환한다.
 */
export const computeGroupGeometry = (
  children: string[],
  items: Record<string, EditorStarterItem>,
  ancestors: ReadonlySet<string> = new Set(),
): GroupGeometry => {
  const members = children
    .map((id) => {
      const item=items[id];
      if(!item||ancestors.has(id))return undefined;
      return item.type==='group'
        ? {...item,...resolveGroupGeometry(item,items,new Set([...ancestors,id]))}
        : item;
    })
    .filter((m): m is EditorStarterItem => Boolean(m));
  if (members.length === 0) {
    return {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      from: 0,
      durationInFrames: 0,
    };
  }
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  let from = Infinity;
  let end = -Infinity;
  for (const m of members) {
    minL = Math.min(minL, m.left);
    minT = Math.min(minT, m.top);
    maxR = Math.max(maxR, m.left + m.width);
    maxB = Math.max(maxB, m.top + m.height);
    from = Math.min(from, m.from);
    end = Math.max(end, m.from + m.durationInFrames);
  }
  return {
    left: minL,
    top: minT,
    width: maxR - minL,
    height: maxB - minT,
    from,
    durationInFrames: end - from,
  };
};

/** Cut compounds keep padding/pivot and clip interval. Fresh groups remain children-union based. */
export function resolveGroupGeometry(group:GroupItem,items:Record<string,EditorStarterItem>,ancestors:ReadonlySet<string>=new Set()):GroupGeometry{
  if(group.preserveGroupFrame)return {left:group.left,top:group.top,width:group.width,height:group.height,from:group.from,durationInFrames:group.durationInFrames};
  return computeGroupGeometry(group.children,items,ancestors);
}
